import i18n from '@/i18n';
import { taskAPI } from '@/utils/api';
import { buildRetryTaskApiPatch } from './ipeTaskActions';
import { resolveImg2ImgReferenceForApi } from './img2imgPublicUrl';
import {
  clearAiCopywritingTaskCancel,
} from './aiCopywritingTaskCancel';
import { DEFAULT_AI_COPYWRITING_LANGUAGE, getCopywritingLanguageLabel } from '@/pages/ipe/workbench/constants/aiCopywritingConfig';

async function resolveReferenceUrls(rawRefs) {
  const publicRefs = [];
  for (const raw of rawRefs) {
    const trimmed = String(raw || '').trim();
    if (!trimmed) continue;
    const resolved = await resolveImg2ImgReferenceForApi(trimmed);
    if (resolved) publicRefs.push(resolved);
  }
  return publicRefs;
}

function readCopywritingRetryContext(task) {
  const r = task?.result && typeof task.result === 'object' ? task.result : {};
  const prompt = String(r.prompt || r.titlePresetContent || '').trim();
  const outputCount = Math.max(1, Math.min(4, Number(r.outputCount ?? task.total ?? 1) || 1));
  const language = String(r.language || DEFAULT_AI_COPYWRITING_LANGUAGE).trim() || DEFAULT_AI_COPYWRITING_LANGUAGE;
  const maxCharsN = Number(r.maxChars);
  const maxChars = Number.isFinite(maxCharsN) && maxCharsN > 0
    ? Math.max(1, Math.min(500, Math.floor(maxCharsN)))
    : undefined;
  const previews = Array.isArray(r.referencePreviews) ? r.referencePreviews.filter(Boolean) : [];
  const urls = Array.isArray(r.referenceUrls) ? r.referenceUrls.filter(Boolean) : [];
  const rawRefs = urls.length > 0 ? urls : previews;
  const operatorName = String(r.operatorName || r.operator || task.operator || '').trim();
  const uploadMode = String(r.uploadMode || '').trim().toLowerCase() === 'custom' ? 'custom' : 'default';
  const imageGroups = Array.isArray(r.imageGroups) ? r.imageGroups : [];
  return {
    prompt,
    outputCount,
    language,
    maxChars,
    previews,
    rawRefs,
    operatorName,
    uploadMode,
    imageGroups,
  };
}

/** AI 文案历史重试：重新入队服务端 batch */
export async function retryAiCopywritingTask(task) {
  const taskId = String(task?.id || '').trim();
  if (!taskId) {
    throw new Error(i18n.t('workbench:ipeUtilError.taskInvalid'));
  }

  clearAiCopywritingTaskCancel(taskId);

  const {
    prompt,
    outputCount,
    language,
    maxChars,
    previews,
    rawRefs,
    operatorName,
    uploadMode,
    imageGroups,
  } = readCopywritingRetryContext(task);
  if (!prompt && rawRefs.length === 0 && imageGroups.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.taskMissingStyleOrGoods'));
  }

  const publicRefs = await resolveReferenceUrls(rawRefs);
  const isCustom = uploadMode === 'custom' && imageGroups.length > 0;
  const retryTotal = isCustom ? Math.max(1, imageGroups.length) : outputCount;
  const apiPatch = buildRetryTaskApiPatch({
    total: retryTotal,
    successCount: 0,
    failedCount: 0,
  });

  const initialResult = {
    prompt,
    referencePreviews: previews.length > 0 ? previews : publicRefs,
    referenceUrls: publicRefs,
    uploadMode: isCustom ? 'custom' : 'default',
    ...(isCustom ? { imageGroups } : {}),
    generationMode: publicRefs.length > 0 || isCustom ? 'vision' : 'text',
    outputCount: 1,
    language,
    languageLabel: getCopywritingLanguageLabel(language),
    maxChars: maxChars || undefined,
    operator: operatorName,
    operatorName,
    content: '',
    text: '',
    failureHint: '',
    serverWorker: true,
    batchPhase: 'queued',
  };

  await taskAPI.update(taskId, {
    ...apiPatch,
    result: initialResult,
  });

  await taskAPI.submit(taskId, { force: true });
  return { taskId, outputCount: retryTotal };
}
